import React, { useCallback, useEffect, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import {
  Box,
  Button,
  CardBox,
  CircularProgress,
  DialogModal,
  MenuItem,
  ModalBox,
  PageHeader,
  TextField,
} from 'components';
import { useIsMobile } from 'hooks';

import { TenantCreateEditForm } from './TenantCreateEditForm';

import {
  ContentLoader,
  _,
  styled,
  useNavigate,
  useSelector,
  useTranslation,
} from 'third-party';

import { USER_INPUT_DEBOUNCE_TIME_MS } from 'constants/common';
import { userTenantPaymentListUrl } from 'constants/url';

import { useApp } from 'modules/app';
import { useTenant } from 'modules/tenant';

import { appSelectors, tenantSelectors } from 'selectors';

import { getTenantAvatarUrl, hasRights } from 'utils/app';

import { APITenant, APIUserRight, APIUserType } from 'types/api';

const StyleInput = styled(TextField)`
  .MuiInput-input {
    padding: 0.4rem !important;
    min-width: 18rem;
  }
`;

export const TenantList = () => {
  const { t } = useTranslation();
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const [selectedItem, setSelectedItem] = useState<APITenant | null>(null);

  const { userInfo, selectTenant } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isMobile = useIsMobile();
  const ref = useRef<HTMLInputElement>(null);
  const items = useSelector(tenantSelectors.tenantList);
  const allTenantsLoaded = useSelector(
    tenantSelectors.allTenantsLoaded,
  );
  const tenantFilters = useSelector(
    tenantSelectors.tenantFilters,
  );
  const loadTenantsPending = useSelector(
    tenantSelectors.loadMoreTenantsPending,
  );
  const loadMoreTenantPending = useSelector(
    tenantSelectors.loadMoreTenantsPending,
  );

  const query = tenantFilters.query || '';

  const {
    deleteTenant,
    loadTenants,
    loadMoreTenants,
    updateTenantFilters,
    clearTenantFilters,
    resetTenantFilters,
  } = useTenant();

  useEffect(() => {
    loadTenants();
  }, [tenantFilters, loadTenants]);

  const tenants = useSelector(appSelectors.tenants);
  const navigate = useNavigate();

  const onCardClick = useCallback(
    (item: APITenant) => {
      selectTenant(item.id);
      navigate(userTenantPaymentListUrl(item.id));
    },
    [navigate, selectTenant],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchInputChange = useCallback(
    _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        updateTenantFilters({
          ...tenantFilters,
          query: event.target.value,
        });
      }
    }, USER_INPUT_DEBOUNCE_TIME_MS),
    [updateTenantFilters, tenantFilters],
  );

  useEffect(() => {
    return () => {
      clearTenantFilters();
    };
  }, [clearTenantFilters]);

  const cardMenuItems = (item: APITenant, handleClose: () => void) => {
    const hasAccess = hasRights(
      tenants,
      APIUserRight.ManageTenant,
      item.id,
    );

    if (!hasAccess) {
      return undefined;
    }

    return [
      <MenuItem
        key="Edit"
        onClick={() => {
          setSelectedItem(item);
          setIsModalOpen(true);
          handleClose();
        }}
      >
        <EditOutlinedIcon />
        {t('tenantList.menuItem.edit')}
      </MenuItem>,
      <MenuItem
        key="Remove"
        onClick={() => {
          setSelectedItem(item);
          handleClose();
        }}
      >
        <DeleteOutlineOutlinedIcon />
        {t('tenantList.menuItem.remove')}
      </MenuItem>,
    ];
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const bottom =
        Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
        1;

      if (bottom && !loadMoreTenantPending && !allTenantsLoaded) {
        loadMoreTenants();
      }
    },
    [allTenantsLoaded, loadMoreTenantPending, loadMoreTenants],
  );

  const onFilterReset = useCallback(() => {
    resetTenantFilters();
    if (ref.current) {
      ref.current.value = '';
    }
  }, [resetTenantFilters]);

  const getSubTitle = useCallback(
    (item: APITenant) => {
      const userTenant = tenants?.find(
        it => it.tenant.id == item.id,
      );
      if (!userTenant) {
        return t('tenantList.userRole.notInTheTenant');
      }

      return userTenant.userType == APIUserType.Admin
        ? t('tenantList.userRole.admin')
        : t('tenantList.userRole.user');
    },
    [tenants, t],
  );

  const onAddTenantClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    userInfo();
    setIsModalOpen(false);
    setSelectedItem(null);
  }, [userInfo]);

  return (
    <Box width="100%" overflow="auto" onScroll={handleScroll}>
      <Box
        position="sticky"
        top={0}
        zIndex={11}
        bgcolor="white"
        alignItems="center"
      >
        <PageHeader
          header={t('tenantList.header')}
          subHeader={t('tenantList.subHeader')}
        >
          <>
            {isFilteringOn && (
              <Button variant="text" onClick={onFilterReset}>
                <ClearIcon />
              </Button>
            )}
            <Button variant="text" onClick={onAddTenantClick}>
              <AddIcon />
            </Button>
            <Button variant="text" onClick={() => setIsFilteringOn(it => !it)}>
              {isFilteringOn ? <FilterAltIcon /> : <FilterAltOffIcon />}
            </Button>
          </>
        </PageHeader>

        {isFilteringOn && (
          <Box padding="1rem 1rem 0 0 ">
            <SearchOutlinedIcon color="primary" />
            <StyleInput
              inputRef={ref}
              autoComplete="off"
              autoCorrect="off"
              id="input-with-sx"
              onChange={onSearchInputChange}
              variant="standard"
            />
          </Box>
        )}
      </Box>
      {loadTenantsPending && (
        <Box
          paddingTop="1rem"
          display="flex"
          flexWrap="wrap"
          gap="1.5rem"
          sx={{ overflow: 'hidden' }}
        >
          {Array.from({ length: 8 }, (_, index) => (
            <Box
              key={`skeleton-${index}`}
              width="17rem"
              height="13rem"
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <ContentLoader
                speed={2}
                width={272}
                height={208}
                viewBox="0 0 272 208"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                {/* Header section with avatar and title */}
                <circle cx="40" cy="40" r="20" />
                <rect x="70" y="25" rx="4" ry="4" width="120" height="14" />
                <rect x="70" y="45" rx="4" ry="4" width="80" height="10" />

                {/* Description section */}
                <rect x="20" y="80" rx="4" ry="4" width="220" height="10" />
                <rect x="20" y="95" rx="4" ry="4" width="200" height="10" />
                <rect x="20" y="110" rx="4" ry="4" width="180" height="10" />

                {/* Menu icon area */}
                <rect x="220" y="160" rx="4" ry="4" width="24" height="24" />
              </ContentLoader>
            </Box>
          ))}
        </Box>
      )}
      {!loadTenantsPending && (
        <Box
          paddingTop="1rem"
          display="flex"
          flexWrap="wrap"
          gap="1.5rem"
          sx={{ overflow: 'hidden' }}
        >
          {items?.map(it => (
            <CardBox<APITenant>
              item={it}
              key={it.id}
              title={it.name}
              stripeText={
                it.isSandbox ? t('tenantList.sandboxLabel') : undefined
              }
              avatarUrl={getTenantAvatarUrl(it)}
              width="17rem"
              height="13rem"
              subTitle={getSubTitle(it)}
              description={it.description}
              descriptionMaxLines={3}
              onClick={onCardClick}
              menuItems={cardMenuItems}
              highlightQuery={query}
            />
          ))}
        </Box>
      )}
      {loadMoreTenantPending && (
        <Box display="flex" justifyContent="space-around" padding="1rem">
          <CircularProgress />
        </Box>
      )}
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '40rem'}
          height="35rem"
          onClose={onModalClose}
        >
          <TenantCreateEditForm
            onModalClose={onModalClose}
            tenantId={selectedItem?.id}
          />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={!!selectedItem && !isModalOpen}
        description={t('tenantList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deleteTenant(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
    </Box>
  );
};
